import React, { useState, useEffect } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Button } from "@chakra-ui/react";
import { db } from "../../firebase.config";
import { getDocs, collection } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import ScaleSpinner from "../feedback/ScaleSpinner";

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

const FeaturedBikes = () => {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBikes = async () => {
      try {
        setLoading(true);
        const carsRef = collection(db, "featured-bikes");
        const qSnap = await getDocs(carsRef);
        const bikesArray = [];
        qSnap.forEach((doc) => {
          bikesArray.push({ id: doc.id, ...doc.data() });
        });
        setBikes(bikesArray);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to load featured bikes. Please try again later.");
      }
    };

    fetchBikes();
  }, []);

  const checkDetails = (id) => {
    navigate(`/category/sale/${id}?type=featured-bike`);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Featured Bikes For Sale
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover our premium selection of high-quality bikes at competitive prices
          </p>
        </div>

        {error ? (
          <div className="text-center py-12">
            <p className="text-red-500 text-lg bg-red-50 inline-block px-6 py-3 rounded-lg">
              {error}
            </p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center h-64">
            <ScaleSpinner size={15} color="#3b82f6" />
          </div>
        ) : bikes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg bg-gray-50 inline-block px-6 py-3 rounded-lg">
              No featured bikes available at the moment
            </p>
          </div>
        ) : (
          <>
            <Carousel
              responsive={responsive}
              swipeable={true}
              draggable={true}
              showDots={false}
              infinite={true}
              autoPlay={true}
              autoPlaySpeed={4000}
              keyBoardControl={true}
              customTransition="all .5"
              transitionDuration={500}
              containerClass="carousel-container pb-4"
              removeArrowOnDeviceType={["tablet", "mobile"]}
              itemClass="px-3"
            >
              {bikes.map((bike) => (
                <div
                  key={bike.id}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-100"
                >
                  <div className="relative h-56 w-full overflow-hidden group">
                    <img
                      src={bike.carImages?.[0] || "https://via.placeholder.com/400x300?text=No+Image"}
                      alt={bike.modelName}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-5 flex-grow">
                    <h3 className="text-xl font-bold text-gray-900 mb-1 line-clamp-1">
                      {bike.modelName}
                    </h3>
                    <div className="flex items-center mb-2">
                      <svg
                        className="w-4 h-4 text-gray-500 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="text-gray-600 text-sm">{bike.location}</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600 mb-3">
                      PKR {bike.price?.toLocaleString() || "N/A"}
                    </p>
                  </div>
                  <div className="px-5 pb-5">
                    <Button
                      onClick={() => checkDetails(bike.id)}
                      color="white"
                      bg="linear-gradient(to right, #2563eb, #1e40af)"
                      _hover={{
                        bg: "linear-gradient(to right, #1e40af, #1e3a8a)",
                      }}
                      className="w-full font-medium py-2 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-md hover:shadow-lg"
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </Carousel>

            <div className="text-center mt-10">
              <Link to="/featured-bikes">
                <button className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-semibold py-3 px-8 rounded-full shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl inline-flex items-center">
                  Browse All Bikes
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedBikes;